import { isGeoPointValid, normalizeGeoPoint } from '@@partner:helpers/util'
import { GeoPoint, GeoZone } from '@@partner:types:app'
import L, { LatLngLiteral, Marker as LeafletEditableMarker,
  Polygon as LeafletEditablePolygon,
  Map as LeafletMap, } from 'leaflet'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { MapContainer, Marker, Polygon, Popup, TileLayer } from 'react-leaflet'

import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { getCenter} from 'geolib'
import AppLoader from '@components/common/AppLoader'
import { generatePath, Link, useParams } from 'react-router-dom'
import MapMarker from '@components/common/MapMarker'
import { ROUTE_ORDERS_VIEW_ORDER } from '@@partner:config/routes'
import { useLazyFetchZonesInGeoPolygon } from '@@partner:graphql/query/deliveryZone/zonesInGeoPolygon.gql'
import { useLazyFetchShopsInGeoPolygon } from '@@partner:graphql/query/shop/shopsInGeoPolygon.gql'


export const DEFAULT_MAP_CENTER: GeoPoint = {
  latitude: 50.326044,
  longitude: 40.480974
}

/*
  Default map zoom
*/

const DEFAULT_MAP_ZOOM = 3

/*
  Convert polygon coords into leaflet polygon
*/

const makePolygonFromZone = (zone: GeoPoint[]) =>
  L.polygon(convertGeoPointsZoneToLeafletZone(zone), { stroke: true })

/*
  Convert geo zone to leaflet compatible positions
*/

const convertGeoPointsZoneToLeafletZone = (zone: GeoPoint[]) =>
  zone.map((value) => convertGeoPointToLeafletLatLng(value))

/*
  Convert our point format to leaflet compatible
*/

const convertGeoPointToLeafletLatLng = (point: GeoPoint): LatLngLiteral => ({
  lat: point.latitude as number,
  lng: point.longitude as number
})

/*
  Convert leaflet coords to our format
*/

const convertLeafletLatLngToGeoPoint = (
  latLngExpr: LatLngLiteral
): GeoPoint => normalizeGeoPoint({
  latitude: latLngExpr.lat,
  longitude: latLngExpr.lng
})

/*
  Get map center by centerPoint
*/

const makeMapCenter = (centerPoint: GeoPoint): LatLngLiteral =>
  convertGeoPointToLeafletLatLng(
    isGeoPointValid(centerPoint) ? centerPoint : DEFAULT_MAP_CENTER
  )

export default function ActiveOrdersOnMap () {
  const { brandSlug } = useParams<any>()
  // map center state
  const [ mapCenter, setMapCenter ] = useState(() => makeMapCenter(DEFAULT_MAP_CENTER))
  // store map ref
  const [ map, setMap ] = useState<LeafletMap>()

  const { data, loading, error } = useFetchOrders({
    allActive: true
  })
  const [ fetchZones, { data: zonesData, loading: loadingZonesData } ] = useLazyFetchZonesInGeoPolygon()
  const [ fetchShops, { data: shopsData, loading: loadingShops } ] =
    useLazyFetchShopsInGeoPolygon()


  const positions = data?.orders?.edges.map(order => ({
    point: convertGeoPointToLeafletLatLng(order.customerGeoPoint),
    ...order
  })) ?? []
  const center = getCenter(positions.map(position => position.point))
  const preparedCenter = center ? convertGeoPointToLeafletLatLng(center) : mapCenter

  const loadForBounds = useCallback(
    (map: L.Map) => {
      const bounds = map.getBounds()
      const zone = [
        convertLeafletLatLngToGeoPoint(bounds.getNorthWest()),
        convertLeafletLatLngToGeoPoint(bounds.getNorthEast()),
        convertLeafletLatLngToGeoPoint(bounds.getSouthEast()),
        convertLeafletLatLngToGeoPoint(bounds.getSouthWest())
      ]
      fetchZones({
        variables: {
          corners: zone
        }
      })
      fetchShops({
        variables: {
          corners: zone
        }
      })
    },
    [ fetchZones, fetchShops ]
  )

  useLayoutEffect(() => {
    if (map) {
      map.on('dragend', () => loadForBounds(map))
    }
  }, [ map, loadForBounds ])

  const whenMapCreated = (map: LeafletMap) => {
    setMap(map)
    loadForBounds(map)
    map.setView(preparedCenter, 12)
  } 

  if (loading) {
    return <AppLoader.Inline />
  }

  return (
    <MapContainer
      center={preparedCenter}
      zoom={DEFAULT_MAP_ZOOM}
      scrollWheelZoom={false}
      whenCreated={whenMapCreated}
      editable={true}
      style={{
        zIndex: 30,
        height: '100vh',
        width: '100%'
      }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {positions && positions.length > 0 && (
        positions.map(position => (
          <Marker
            icon={MapMarker('blue')}
            position={position.point}
          >
            <Popup>
              <div>{ position.id }</div>
              <div>{ position.address }</div>
              <Link 
                to={generatePath(ROUTE_ORDERS_VIEW_ORDER, {
                  brandSlug,
                  orderId: position.id
                })}
                target='_blank'
              >
                Открыть
              </Link>
            </Popup>
          </Marker>
        ))
      )}

      {zonesData && zonesData.zonesInGeoPolygon.length > 0 && (
        zonesData.zonesInGeoPolygon.map((zone, index) => (
          <Polygon
            key={`polygon:${index}`}
            positions={convertGeoPointsZoneToLeafletZone(zone.zoneGeoDeliveryZone)}
            pathOptions={{
              stroke: true,
              color: '#e9833a'
            }}
          />
        ))
      )}
    </MapContainer>
  )
}